import React from "react";
import { observer, useLocalObservable } from "mobx-react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
  Theme,
} from "@mui/material";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../../../../styles/Shipmentstyles";
import { OptionApi } from "../../../../../models/CatalogOptions/types";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { useStores } from "../../../use-stores";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import { SortType } from "../../../../../models/SortType";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";

export interface CatalogOptionsTableProps {
  maxHeight: number | string;
  options: OptionApi[];
  limit: number;
  page_no: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
  onEdit: (option: OptionApi) => void;
  onDelete: (option: OptionApi) => void;
}

function CatalogOptionsTable({
  maxHeight,
  options,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onEdit,
  onDelete,
}: CatalogOptionsTableProps) {
  const navigate = useNavigate();
  const theme = useTheme<Theme>();
  const { userStore } = useStores();
  const canEdit = userStore.hasPermission(EmployeeRoles.EDIT_CATALOG);

  const ui = useLocalObservable(() => ({
    anchorEl: null as HTMLElement | null,
    currentOption: null as OptionApi | null,
  }));

  const isOpen = Boolean(ui.anchorEl);

  const handleSort = (column: string) => {
    onSortChange(
      column,
      sortBy === column && sortType === SortType.DESC
        ? SortType.ASC
        : SortType.DESC,
    );
  };

  const goToDetail = (id: number) => navigate(`/catalog-options/${id}`);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    option: OptionApi,
  ) => {
    ui.anchorEl = event.currentTarget;
    ui.currentOption = option;
  };

  const handleMenuClose = () => {
    ui.anchorEl = null;
    ui.currentOption = null;
  };

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  return (
    <Paper
      elevation={0}
      sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}
    >
      <TableContainer
        sx={{
          maxHeight,
          maxWidth: "100%",
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Table stickyHeader size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <AppTableHeaderCell>
                <TableSortLabel
                  active={sortBy === "name"}
                  direction={
                    sortBy === "name" && sortType === SortType.ASC
                      ? "asc"
                      : "desc"
                  }
                  onClick={() => handleSort("name")}
                >
                  Name
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell>Slug</AppTableHeaderCell>
              <AppTableHeaderCell>Display type</AppTableHeaderCell>
              <AppTableHeaderCell>
                <TableSortLabel
                  active={sortBy === "priority"}
                  direction={
                    sortBy === "priority" && sortType === SortType.ASC
                      ? "asc"
                      : "desc"
                  }
                  onClick={() => handleSort("priority")}
                >
                  Position
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell>
                <TableSortLabel
                  active={sortBy === "active"}
                  direction={
                    sortBy === "active" && sortType === SortType.ASC
                      ? "asc"
                      : "desc"
                  }
                  onClick={() => handleSort("active")}
                >
                  Active
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell>Updated</AppTableHeaderCell>
              <AppTableHeaderCell txtAlign="right">Actions</AppTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {options.length === 0 ? (
              <StyledTableRow>
                <StyledTableCell colSpan={7} align="center">
                  <Typography
                    variant="body2"
                    color={theme.palette.customColors?.greyTextColor}
                    sx={{ py: 4 }}
                  >
                    No catalog options found.
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            ) : (
              options.map((row) => (
                <StyledTableRow
                  key={row.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => goToDetail(row.id)}
                >
                  <AppTableRowCell>
                    <Typography variant="body2" fontWeight={600}>
                      {row.name}
                    </Typography>
                  </AppTableRowCell>
                  <AppTableRowCell>{row.slug || "—"}</AppTableRowCell>
                  <AppTableRowCell>
                    <Chip
                      size="small"
                      label={row.display_type}
                      variant="outlined"
                      sx={{
                        height: 22,
                        fontSize: "0.7rem",
                        textTransform: "capitalize",
                      }}
                    />
                  </AppTableRowCell>
                  <AppTableRowCell>{row.position}</AppTableRowCell>
                  <AppTableRowCell>
                    <Chip
                      size="small"
                      label={row.is_active ? "Active" : "Inactive"}
                      color={row.is_active ? "success" : "default"}
                      variant={row.is_active ? "filled" : "outlined"}
                      sx={{
                        height: 22,
                        fontSize: "0.65rem",
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </AppTableRowCell>
                  <AppTableRowCell>
                    {row.modified_date ? formatDate(row.modified_date) : "—"}
                  </AppTableRowCell>
                  <AppTableRowCell onClick={(e) => e.stopPropagation()}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-end"
                      gap={0.5}
                    >
                      {canEdit ? (
                        <Tooltip
                          title={
                            row.is_active
                              ? "Delete option"
                              : "Option already inactive"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={!row.is_active}
                              onClick={() => onDelete(row)}
                              sx={{
                                color: theme.palette.customColors?.errorRed,
                                border: `1px solid ${theme.palette.customColors?.greyBorder}`,
                                borderRadius: 1.5,
                              }}
                              aria-label={`Delete ${row.name}`}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                    </Box>
                  </AppTableRowCell>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ px: 1 }}>
        <AppTablePagenation
          totalCount={total_count}
          pageNo={page_no}
          limit={limit}
          onPageChange={onPageChange}
          handleRowsChange={handleRowsChange}
        />
      </Box>
    </Paper>
  );
}

export default observer(CatalogOptionsTable);
